import { create } from 'zustand'
import apiHelper from '@/network/api'

interface NotificationChannelConfiguration {
  [key: string]: any
}

interface NotificationChannel {
  id: string
  name: string
  type: string
  configuration: NotificationChannelConfiguration
  isActive: boolean
  createdAt: string
}

interface NotificationChannelResponse {
  slack: NotificationChannel[]
  discord: NotificationChannel[]
  line: NotificationChannel[]
  email: NotificationChannel[]
}

interface NotificationChannelState {
  channels: NotificationChannel[]
  isLoading: boolean
  error: string | null
  fetchChannels: () => Promise<void>
  createChannel: (channel: Omit<NotificationChannel, 'id' | 'createdAt' | 'updatedAt' | 'isActive'>) => Promise<{
    success: boolean
    data?: NotificationChannel
    error?: string
    errorCode?: number
  }>
  updateChannel: (id: string, channel: Partial<Omit<NotificationChannel, 'id' | 'createdAt' | 'updatedAt'>>) => Promise<{
    success: boolean
    error?: string
  }>
  deleteChannel: (id: string) => Promise<{
    success: boolean
    error?: string
  }>
}

const useNotificationChannelStore = create<NotificationChannelState>((set, get) => ({
  channels: [],
  isLoading: false,
  error: null,

  fetchChannels: async () => {
    try {
      set({ isLoading: true, error: null })
      const response = await apiHelper(null).get('/notification_channel')
      
      if (response.status === 200) {
        const data = response.data as NotificationChannelResponse
        const allChannels = [...(data.slack || []), ...(data.discord || []), ...(data.line || []), ...(data.email || [])]
        
        set({ 
          channels: allChannels,
          isLoading: false 
        })
      }
    } catch (error: any) {
      set({ 
        error: 'Failed to fetch notification channels',
        isLoading: false
      })
      console.error('Error fetching notification channels:', error)
    }
  },

  createChannel: async (channel) => {
    try {
      const response = await apiHelper(null).post('/notification_channel/', channel)
      
      if (response.status === 201 || response.status === 200) {
        await get().fetchChannels()
        return { 
          success: true,
          data: response.data
        }
      }
      return { success: false }
    } catch (error: any) {
      console.error('Error creating notification channel:', error)
      const data = error.response?.data
      return {
        success: false,
        error: data?.message || 'Failed to create notification channel',
        errorCode: data?.error,
      }
    }
  },

  updateChannel: async (id, channel) => {
    try {
      const response = await apiHelper(null).put(`/notification_channel/${id}`, channel)
      
      if (response.status === 200) {
        await get().fetchChannels()
        return { success: true }
      }
      return { success: false }
    } catch (error: any) {
      console.error('Error updating notification channel:', error)
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to update notification channel'
      }
    }
  },

  deleteChannel: async (id) => {
    try {
      const response = await apiHelper(null).delete(`/notification_channel/${id}`)
      
      if (response.status === 204 || response.status === 200) {
        await get().fetchChannels()
        return { success: true }
      }
      return { success: false }
    } catch (error: any) {
      console.error('Error deleting notification channel:', error)
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to delete notification channel'
      }
    }
  }
}))

export default useNotificationChannelStore
